import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { statusColors } from '../App';
import { apiService } from '../services/apiService';

interface Batch {
  id: number;
  batch_number: string;
  status: string;
  product_name: string;
  mbr_number: string;
  line_name: string;
  batch_size_value: number;
  batch_size_unit: string;
  actual_end: string;
  completed_by: string;
  notes: string;
}

const QAReview: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState<number | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    fetchQAReviewBatches();
  }, []);

  const fetchQAReviewBatches = async () => {
    try {
      setLoading(true);
      const data = await apiService.getQAReviewBatches();
      setBatches(data.results || data);
    } catch (error) {
      console.error('Error fetching QA review batches:', error);
      setAlert({ type: 'error', message: 'Failed to load batches for review' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedBatch) return;

    try {
      setProcessing(selectedBatch.id);
      await apiService.approveBatch(selectedBatch.id);
      setAlert({ type: 'success', message: `Batch ${selectedBatch.batch_number} approved successfully` });
      await fetchQAReviewBatches();
      setApproveDialogOpen(false);
      setSelectedBatch(null);
    } catch (error) {
      console.error('Error approving batch:', error);
      setAlert({ type: 'error', message: 'Failed to approve batch' });
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!selectedBatch) return;

    try {
      setProcessing(selectedBatch.id);
      await apiService.rejectBatch(selectedBatch.id, rejectReason);
      setAlert({ type: 'success', message: `Batch ${selectedBatch.batch_number} rejected` });
      await fetchQAReviewBatches();
      setRejectDialogOpen(false);
      setSelectedBatch(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting batch:', error);
      setAlert({ type: 'error', message: 'Failed to reject batch' });
    } finally {
      setProcessing(null);
    }
  };

  const openApproveDialog = (batch: Batch) => {
    setSelectedBatch(batch);
    setApproveDialogOpen(true);
  };

  const openRejectDialog = (batch: Batch) => {
    setSelectedBatch(batch);
    setRejectDialogOpen(true);
  };

  const closeDialogs = () => {
    setApproveDialogOpen(false);
    setRejectDialogOpen(false);
    setSelectedBatch(null);
    setRejectReason('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h3" component="h1" gutterBottom>
        QA Review
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Review and approve or reject completed batches
      </Typography>

      {alert && (
        <Alert 
          severity={alert.type} 
          sx={{ mb: 3 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Batches Awaiting Review
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>MBR</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Line</TableCell>
                    <TableCell>Batch Size</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Completed By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="textSecondary">
                          No batches pending review
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch) => (
                      <TableRow key={batch.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {batch.batch_number}
                          </Typography>
                        </TableCell>
                        <TableCell>{batch.mbr_number}</TableCell>
                        <TableCell>{batch.product_name}</TableCell>
                        <TableCell>{batch.line_name}</TableCell>
                        <TableCell>
                          {batch.batch_size_value} {batch.batch_size_unit}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(batch.actual_end)}
                          </Typography>
                        </TableCell>
                        <TableCell>{batch.completed_by}</TableCell>
                        <TableCell>
                          <Chip
                            label={batch.status.replace('_', ' ')}
                            size="small"
                            sx={{
                              backgroundColor: statusColors[batch.status as keyof typeof statusColors],
                              color: 'white',
                              textTransform: 'capitalize',
                              fontWeight: 'medium',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<ApproveIcon />}
                              onClick={() => openApproveDialog(batch)}
                              disabled={processing === batch.id}
                              sx={{ textTransform: 'none' }}
                            >
                              {processing === batch.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                'Approve'
                              )}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<RejectIcon />}
                              onClick={() => openRejectDialog(batch)}
                              disabled={processing === batch.id}
                              sx={{ textTransform: 'none' }}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={closeDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Batch</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to approve batch <strong>{selectedBatch?.batch_number}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            This action will mark the batch as approved and complete the production process.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs}>Cancel</Button>
          <Button 
            onClick={handleApprove} 
            variant="contained" 
            color="success"
            disabled={processing !== null}
          >
            Approve Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={closeDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Batch</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to reject batch <strong>{selectedBatch?.batch_number}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            margin="normal"
            required
            helperText="Please provide a detailed reason for rejection"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs}>Cancel</Button>
          <Button 
            onClick={handleReject} 
            variant="contained" 
            color="error"
            disabled={!rejectReason.trim() || processing !== null}
          >
            Reject Batch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QAReview;